import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { CanActivate } from '@angular/router';
import { isPlatformServer } from '@angular/common';

import { Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';

import { isPresent } from '@fcom/core/utils';
import { acceptTerms, paymentStatus } from '@fcom/common-booking/store/selectors';
import { orderFetchInformation } from '@fcom/common-booking/store/selectors/order.selector';
import { PaymentStatus } from '@fcom/common-booking/interfaces';
import { IdAndHash } from '@fcom/dapi';
import { BookingAndAppState } from '@fcom/common/interfaces/booking';

import { MmbStage } from '../../../enums';
import { getStage, StateActions } from '../../../store';

@Injectable({ providedIn: 'root' })
export class AncillaryPurchaseCheckoutGuard implements CanActivate {
  constructor(
    private store$: Store<BookingAndAppState>,
    @Inject(PLATFORM_ID) private platform: Record<string, unknown>
  ) {}

  canActivate(): Observable<boolean> {
    if (isPlatformServer(this.platform)) {
      return of(true);
    }

    return combineLatest([
      this.store$.pipe(getStage()),
      this.store$.pipe(paymentStatus()),
      this.store$.pipe(orderFetchInformation()),
      this.store$.pipe(acceptTerms()),
    ]).pipe(
      take(1),
      map(
        ([stage, paymentStatus, orderFetchInformation, termsAndCondition]: [
          MmbStage,
          PaymentStatus,
          IdAndHash,
          boolean,
        ]) => {
          if (this.paymentCheck(paymentStatus, orderFetchInformation, termsAndCondition)) {
            if (stage !== MmbStage.CHECKOUT) {
              this.store$.dispatch(StateActions.setStage({ stage: MmbStage.CHECKOUT }));
            }
            return true;
          }
          if (!termsAndCondition) {
            this.store$.dispatch(StateActions.setStage({ stage: MmbStage.REVIEW }));
            return false;
          }
          this.store$.dispatch(StateActions.setStage({ stage: MmbStage.LOGIN }));
          return false;
        }
      )
    );
  }

  private paymentCheck(
    paymentStatus: PaymentStatus,
    orderFetchInformation: IdAndHash,
    isTermsAndConditionsAccepted: boolean
  ): boolean {
    return (
      isTermsAndConditionsAccepted &&
      isPresent(orderFetchInformation) &&
      isPresent(orderFetchInformation.id) &&
      isPresent(orderFetchInformation.hash) &&
      PaymentStatus.PENDING_VERIFICATION !== paymentStatus
    );
  }
}
